import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Users, AlertTriangle, Mic, ArrowRight } from "lucide-react";
import { speechSupported } from "@/hooks/useSpeechRecognition";
import { getStats } from "@/lib/api";

const BridgeLogo = () => (
  <svg viewBox="0 0 64 40" className="h-10 w-16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 30h60" />
    <path d="M6 30c0-14 10-22 26-22s26 8 26 22" />
    <path d="M14 30c0-8 6-13 18-13s18 5 18 13" />
    <path d="M32 8v22M20 14v16M44 14v16" />
    <path d="M2 36h60" strokeOpacity="0.35" strokeDasharray="4 5" />
  </svg>
);

const ModeCard = ({ to, icon: Icon, badge, title, desc, testId, tone, btn }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group relative flex flex-col items-center gap-4 rounded-3xl border p-7 text-center transition-[border-color,background-color] duration-200 ${tone}`}
  >
    <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">{badge}</span>
    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
      <Icon className="h-7 w-7" />
    </span>
    <h2 className="text-xl font-extrabold tracking-tight">{title}</h2>
    <p className="text-sm leading-relaxed text-slate-300">{desc}</p>
    <span className={`mt-auto inline-flex h-11 items-center gap-3 rounded-full pl-6 pr-1.5 text-sm font-bold transition-colors duration-200 ${btn}`}>
      Empezar
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 transition-transform duration-300 group-hover:translate-x-1 group-hover:bg-white/30">
        <ArrowRight className="h-4 w-4" />
      </span>
    </span>
  </Link>
);

export default function Home() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    getStats().then(setStats).catch(() => {});
  }, []);

  return (
    <main className="grain min-h-full bg-[#0b132b] px-5 pb-12 pt-10 text-white sm:px-10" data-testid="home-page">
      <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
        <div className="flex flex-col items-center gap-2 text-amber-400" data-testid="brand-logo">
          <BridgeLogo />
          <span className="text-sm font-extrabold uppercase tracking-[0.35em]">
            Qantara <span className="font-arabic text-base normal-case tracking-normal text-amber-300/80">· قنطرة</span>
          </span>
        </div>
        <h1 className="mt-6 max-w-2xl text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          Español <span className="text-amber-400">↔</span> <span className="font-arabic">الدارجة</span>
          <br />
          en tiempo real.
        </h1>
        <p className="mt-5 max-w-full whitespace-nowrap text-[clamp(0.45rem,2.4vw,1.125rem)] text-slate-300" data-testid="home-subtitle-es">
          Transcripción y traducción instantánea español-darija, darija-español
        </p>
        <p dir="rtl" className="font-arabic mt-2 max-w-full whitespace-nowrap text-[clamp(0.6rem,3vw,1.25rem)] text-amber-200/90" data-testid="home-subtitle-ar">
          نقل وترجمة فورية من الإسبانية للدارجة، ومن الدارجة للإسبانية
        </p>

        {!speechSupported && (
          <div
            data-testid="browser-unsupported-alert"
            className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-left text-sm text-amber-200"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <p>
              Este navegador no soporta reconocimiento de voz. Usa <strong>Chrome</strong>, <strong>Edge</strong> o el navegador de{" "}
              <strong>Android</strong> para que funcione el micrófono.
            </p>
          </div>
        )}

        <div className="mt-10 grid w-full gap-5 sm:grid-cols-2">
          <ModeCard
            to="/conversar"
            icon={Smartphone}
            badge="Cara a cara"
            title="Un solo dispositivo"
            desc="Pantalla dividida. La mitad de abajo se gira 180° para que la persona de enfrente lea el darija cómodamente."
            testId="mode-single-device-button"
            tone="border-blue-500/30 bg-[#131c3a] hover:border-blue-400/60 hover:bg-[#16224a]"
            btn="bg-blue-600 text-white group-hover:bg-blue-500"
          />
          <ModeCard
            to="/sala"
            icon={Users}
            badge="Varios dispositivos"
            title="Sala con código o QR"
            desc="Crea una sala, comparte el QR o el código de 6 letras y todos los móviles que quieras siguen la conversación, cada uno en su idioma."
            testId="mode-two-devices-button"
            tone="border-blue-500/30 bg-[#131c3a] hover:border-blue-400/60 hover:bg-[#16224a]"
            btn="bg-blue-600 text-white group-hover:bg-blue-500"
          />
        </div>

        <div className="mt-10 flex w-full justify-center">
          <Link
            to="/conversar"
            data-testid="home-mic-start-button"
            className="flex h-16 items-center gap-3 rounded-full bg-emerald-600 px-9 text-lg font-bold text-white transition-colors duration-200 hover:bg-emerald-500"
          >
            <Mic className="h-6 w-6" />
            Hablar ahora
          </Link>
        </div>

        {stats && (
          <p className="mt-8 text-center text-xs text-slate-500" data-testid="stats-line">
            {stats.requests} traducciones servidas · {stats.cache_hits} reutilizadas desde caché (sin coste)
          </p>
        )}
      </div>
    </main>
  );
}
